// MediaSkeleton — the shared "media is still loading" placeholder (bug #916).
// A skeleton-shimmer footprint (same size the media will occupy once it
// decodes, so nothing jumps on swap) with the on-brand Wheel loader
// (BrandWheelLoader) centred on top — never a generic MUI CircularProgress
// (wheels-dont-spin / brand law, see Thread.historyLoaderBrandLaw.test.jsx).
// Reused by chat media attachments and wheel media blocks (Image/Video) so
// every "media takes a long time to load" surface reads the same way.
import React from "react";
import styled, { keyframes } from "styled-components";
import BrandWheelLoader from "../BrandWheelLoader";

const shimmer = keyframes`
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
`;

const SkeletonWrap = styled.div`
  position: ${({ $overlay }) => ($overlay ? "absolute" : "relative")};
  inset: ${({ $overlay }) => ($overlay ? 0 : "auto")};
  width: 100%;
  min-height: ${({ $overlay, $minHeight }) => ($overlay ? "100%" : `${$minHeight}px`)};
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(90deg, #efe9fe 25%, #f6f7f9 37%, #efe9fe 63%);
  background-size: 400% 100%;
  animation: ${shimmer} 1.4s ease infinite;
  border-radius: ${({ $borderRadius }) => $borderRadius}px;
  overflow: hidden;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// `overlay`: absolute-positioned to cover a same-footprint sibling media
// element (the img/video stays mounted underneath — same pattern as the
// existing ImageUploadingOverlay — so nothing needs to be conditionally
// unmounted/hidden and no layout jump happens on swap). Non-overlay: a
// normal block placeholder that occupies the space itself (used where no
// media element is mounted yet).
// `loaderSize`: overrides BrandWheelLoader's fixed variant step-size (e.g.
// `variant="inline"` => 20px) with an exact pixel diameter. Optional — omit it
// and the mark keeps its usual step size, unaffected. UserImage passes the
// avatar's own `size` here so the wheel-loader mark's circumference exactly
// overlays the avatar-wheel ring it stands in for, at every avatar size
// (ISP-9999, Gordon 2026-08-27 — "loader too small" for Avatar Wheels).
// `loop`: forwarded to BrandWheelLoader unchanged (default true — every
// existing consumer keeps the indeterminate arc-sweep). audit-10091824-T3.6:
// a multi-second upload driving `isLoading` (real determinate progress
// elsewhere on screen) needs `loop={false}` here too, same reasoning as the
// UploadMedia dropzone's own BrandWheelLoader (SegmentBuilder/Shared/
// UploadMedia/index.jsx) — otherwise this skeleton's loader blanks to an
// empty ring every ~2s cycle boundary (#908 START EMPTY) for the whole
// upload, reading as "stalling/restarting".
const MediaSkeleton = ({
  variant = "medium",
  label = "Loading media…",
  minHeight = 160,
  borderRadius = 0,
  overlay = false,
  className,
  style,
  loaderSize,
  loop = true,
}) => (
  <SkeletonWrap
    data-testid="media-skeleton"
    $overlay={overlay}
    $minHeight={minHeight}
    $borderRadius={borderRadius}
    className={className}
    style={style}
  >
    <BrandWheelLoader variant={variant} label={label} size={loaderSize} loop={loop} />
  </SkeletonWrap>
);

export default MediaSkeleton;
